import { useEffect, useState } from 'react';
import type { Attachment } from '../types';
import { formatBytes } from '../utils';
import { CloseIcon, FileIcon } from './icons';

interface AttachmentChipProps {
  attachment: Attachment;
  /** Omit to render a read-only chip (inside a sent message). */
  onRemove?: () => void;
  removeLabel?: string;
}

export function AttachmentChip({ attachment, onRemove, removeLabel }: AttachmentChipProps) {
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(attachment.url);

  useEffect(() => {
    if (attachment.url) {
      setPreviewUrl(attachment.url);
      return;
    }
    if (!attachment.file || !attachment.mimeType.startsWith('image/')) return;

    const objectUrl = URL.createObjectURL(attachment.file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [attachment.file, attachment.url, attachment.mimeType]);

  const isImage = attachment.mimeType.startsWith('image/') && previewUrl;

  return (
    <div className="cbw-chip" title={`${attachment.name} (${formatBytes(attachment.size)})`}>
      {isImage ? (
        <img className="cbw-chip__thumb" src={previewUrl} alt="" />
      ) : (
        <span className="cbw-chip__icon">
          <FileIcon width={14} height={14} />
        </span>
      )}
      <span className="cbw-chip__text">
        <span className="cbw-chip__name">{attachment.name}</span>
        <span className="cbw-chip__size">{formatBytes(attachment.size)}</span>
      </span>
      {onRemove && (
        <button
          type="button"
          className="cbw-chip__remove"
          onClick={onRemove}
          aria-label={`${removeLabel}: ${attachment.name}`}
        >
          <CloseIcon width={12} height={12} />
        </button>
      )}
    </div>
  );
}
